/*
 * Copyright (c) 2022-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */
import objectPath from "object-path";
import equal from "fast-deep-equal";
import {Notification} from "./Notification.jsx";
import EventEmitter from "events";
import {customAlphabet} from "nanoid";
import {Decimal} from "decimal.js";
import {ErrorHandler} from "./ErrorHandling.js";


/**
 * This component allows to spawn drawers and modals
 * @type {{drawer: (function(*=, *=, *=, *=): {setState: setState, close: close, open: open}), modal: (function(*=, *=, *=, *=): {setState: setState, close: close, open: open})}}
 */
const Util = function () {


    /**
     *    This set of functions allows to work with arrays
     *    @type {{getElementByJsonKey: (function(Object[], string, (string|number|boolean)): Object)}}
     */
    const arrays = function () {

        /**
         * This function returns a element af an array by one of its key value peers
         * @param {object[]} arrayItem - The array in which to search for
         * @param {string} keyName - The name of key in the json
         * @param {string | number | boolean} keyValue - The value
         * @return {object}
         */
        function getElementByJsonKey(arrayItem, keyName, keyValue) {
            return arrayItem[arrayItem.findIndex(item => item[keyName] === keyValue)]

        }

        function insert(array, index) {
            const items = Array.prototype.slice.call(arguments, 2);
            return [].concat(array.slice(0, index), items, array.slice(index));
        }

        function removeDuplicates(array) {
            return [...new Set(array)];
        }

        function removeDuplicatesObjects(array, keyName) {
            // ATTENTION: Before changing check performance
            return [...new Map(array.map(item => [item[keyName], item])).values()]
        }

        /**
         * *
         * @param targetArray
         * @param callback
         * @param chunkSize
         * @returns {Promise<unknown>}
         */
        function chunk(targetArray, callback, chunkSize = 100) {
            return new Promise(async (resolve) => {
                for (let i = 0; i < targetArray.length; i += chunkSize) {
                    const chunk = targetArray.slice(i, i + chunkSize);
                    await Promise.all(chunk.map(async (item) => {
                        return callback(item)
                    }))
                }

                resolve()

            })
        }

        function mapPaged(fetchFunc, callback, chunkSize = 100, maxLength = 1000000) {
            return new Promise(async (resolve) => {
                let result = await fetchFunc(0, chunkSize)
                let currentCount = 0
                do {
                    await Promise.all(result.recordset.map(async (item) => {
                        currentCount++;
                        return await callback(item)
                    }))
                    await Util.time.sleep(chunkSize * 2.5)

                    result = await fetchFunc(currentCount, chunkSize)

                } while (result.maxCount > currentCount && currentCount < maxLength)

                resolve()

            })
        }

        /**
         *
         * @param data {array}
         * @param filter {object[]}
         * @returns {*[]}
         */
        function applyFilter(data = [], filter = []) {
            return data.filter((record) => {
                return (filter ?? []).map((filter) => {
                    let filterValue = record[filter.column] || ""
                    if (filter.type === "is") {
                        return filterValue === (filter.value);
                    } else if (filter.type === "in") {
                        if (filter.value !== "") {
                            return filter.value.split(",").indexOf(filterValue) + 1;
                        } else {
                            return true;
                        }
                    } else if (filter.type === "notin") {
                        if (filter.value !== "") {
                            return !(filter.value.split(",").indexOf(filterValue) + 1);
                        } else {
                            return true;
                        }
                    } else if (filter.type === "isnot") {
                        return filterValue !== (filter.value);
                    } else if (filter.type === "isexact") {
                        return filterValue === (filter.value);
                    } else {
                        return filterValue.includes(filter.value);
                    }
                }).reduce((previousValue, currentValue) => (previousValue && currentValue), true)
            })


        }

        function getNextName(Name, takenNames = []) {
            let itemCount = takenNames.filter(item => ((item ?? "").toString().startsWith(Name)))

            if (itemCount.length === 0) {
                return Name
            }

            return Name + " " + (itemCount.length + 1)

        }

        function selectCarousel(array, newElement) {
            if (array.indexOf(newElement) !== -1) {
                array = array.filter(item => (item !== newElement))
            } else {
                array.push(newElement)
            }
            return array
        }

        function insertOrUpdate(array, newElement, condition) {
            let columnIndex = array.findIndex((item) => condition(item))

            if (columnIndex === -1) {
                array.push(newElement)
            } else {
                array[columnIndex] = newElement
            }
            return array
        }

        /**
         * Converts an array of objects to one object
         * @param array
         * @param initObject
         * @returns {object}
         */
        function reduceToObject(array, initObject = {}) {

            return Object.assign(initObject, ...array);
            // ATTENTION: The performance of this function is critical to the overall performance of the system.

            // let finalObj = initObject;
            //
            // for (let i = 0; i < array.length; i++) {
            //     Object.assign(finalObj, array[i]);
            // }
            // return finalObj

            //return array.reduce((a, b) => ({...a, ...b}), initObject)
        }

        /**
         * This function converts an array of arrays into an array.
         * @param array
         * @returns {array}
         */
        function reduceToArray(array) {

            return array.flat(1)
            // ATTENTION: The performance of this function is critical to the overall performance of the system.

        }


        // return the functions
        return {
            reduceToObject: reduceToObject,
            reduceToArray: reduceToArray,
            getElementByJsonKey: getElementByJsonKey,
            insert: insert,
            removeDuplicates: removeDuplicates,
            removeDuplicatesObjects: removeDuplicatesObjects,
            chunk: chunk,
            selectCarousel: selectCarousel,
            mapPaged: mapPaged, applyFilter: applyFilter, getNextName: getNextName, insertOrUpdate: insertOrUpdate
        }

    }()


    const number = function () {


        function round(number, decimal = 0) {
            let pow = Math.pow(10, decimal);
            return Math.round((Number(number) + Number.EPSILON) * pow) / pow

        }

        function multiply(number, multiplyer = 1) {
            if (number === "" || number === undefined || Number.isNaN(Number.parseInt(number)) || (!(/^\d+(\.\d+)*$/.test(number)))) {
                return 0
            }
            let x = new Decimal(number)
            let result = x.mul(multiplyer)
            return result.toNumber()
        }

        function divide(number, divider = 1) {
            if (number === "" || number === undefined || Number.isNaN(Number.parseInt(number)) || (!(/^\d+(\.\d+)*$/.test(number)))) {
                return 0
            }
            let x = new Decimal(number)
            let result = x.div(divider)
            return result.toNumber()
        }

        function toWordCardinal(number, limit = 20) {

            let ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
                'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
                'seventeen', 'eighteen', 'nineteen'];


            if (number < 0) return number.toString();

            if (number === 0) return 'zero';

            if (number > limit) {
                return number.toString()
            }
            //the case of 1 - 20
            if (number < 20) {
                return ones[number];
            }

        }

        function toWordOrdinal(number, limit = 10) {

            let ones = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];


            if (number < 0) return number.toString();

            if (number === 0) return 'zero';

            if (number > limit) {
                return number.toString()
            }
            //the case of 1 - 20
            if (number < 10) {
                return ones[number];
            }


        }

        function countDecimals(number) {
            if (Math.floor(number) === number) return 0;
            return number?.toString()?.split(".")?.[1]?.length ?? 0;
        }

        // return the functions
        return {
            multiply: multiply,
            countDecimals: countDecimals,
            divide: divide,
            round: round,
            toWordCardinal: toWordCardinal,
            toWordOrdinal: toWordOrdinal,
        }

    }()

    const string = function () {


        function isValid(value) {
            return typeof value === 'string' || value instanceof String
        }

        // return the functions
        return {

            isValid: isValid,
        }

    }()


    /**
     * This set of functions allows to work with objects
     * @type {{flattenObject: (function(*): {}), areEqual: (function(*, *): boolean), getAllObjectPaths: (function(*, *): *[]), isObject: (function(*): *), clone: (function(*): any)}}
     */
    const objects = function () {

        function isValid(value) {
            return typeof value === 'object' && value !== null
        }

        /**
         * Check it tow nested objects are equal
         * @param a
         * @param b
         * @returns {boolean}
         */
        function areEqual(a, b) {
            return equal(a, b)
        }

        function flattenObject(ob) {

            // The object which contains the
            // final result
            let result = {};

            // loop through the object "ob"
            for (const i in ob) {

                // We check the type of the i using
                // typeof() function and recursively
                // call the function again
                if ((typeof ob[i]) === 'object' && !Array.isArray(ob[i])) {
                    const temp = flattenObject(ob[i]);
                    for (const j in temp) {

                        // Store temp in result
                        result[i + '.' + j] = temp[j];
                    }
                } else if (Array.isArray(ob[i])) {
                    ob[i].map((arrayObj, index) => {
                        const temp = flattenObject(arrayObj);
                        for (const j in temp) {

                            // Store temp in result
                            result[i + '.' + index + '.' + j] = temp[j];
                        }
                    })

                }

                // Else store ob[i] in result directly
                else {
                    result[i] = ob[i];
                }
            }
            return result;
        }

        /**
         * Check if the input is a object
         * @param input
         * @returns {boolean}
         */
        function isObject(input) {
            return (typeof input === 'object' &&
                !Array.isArray(input) &&
                input !== null);
        }


        function getAllObjectPaths(obj, prefix) {
            let keys = Object.keys(obj);
            prefix = prefix ? prefix + '.' : '';
            return keys.reduce(function (result, key) {
                if (isObject(obj[key])) {
                    result = result.concat(getAllObjectPaths(obj[key], prefix + key));
                } else if (Array.isArray(obj[key])) {
                    result = result.concat(getAllObjectPaths(obj[key], prefix + key));
                } else {
                    result.push(prefix + key);
                }
                return result;
            }, []);
        }

        const isArray = Array.isArray;
        const objectKeys = Object.keys;


        /**
         * clone a object to remove alle the references
         * @param object
         * @link https://github.com/streamich/fastest-json-copy/blob/main/lib/v2.js
         * @returns {any}
         */
        function clone(object) {
            // this seems to be the fastet way to clone a object
            if (!object) return object;
            if (isArray(object)) {
                let arr = [];
                let length = object.length;
                for (let i = 0; i < length; i++) arr.push(clone(object[i]))
                return arr;
            } else if (typeof object === 'object') {
                let keys = objectKeys(object);
                let newObject = {};
                for (let i = keys.length - 1; i > -1; i--) {
                    let key = keys[i];
                    newObject[key] = clone(object[key]);
                }
                return newObject;
            }
            return object;

            return JSON.parse(JSON.stringify(object))
            return structuredClone(object)


        }


        /**
         * Make a object un changeable (freez)
         * @param object
         * @returns {unknown[]}
         */
        function freez(object) {

            return Object.freeze(object);

        }

        /**
         * This is a fast clone witch only clones a object but no arrays
         * @param object
         * @returns {*}
         */
        function noArrayClone(object) {

            return {...object};

        }

        function tryParse(jsonString, fallback = false) {
            try {
                let o = JSON.parse(jsonString);

                // Handle non-exception-throwing cases:
                // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
                // but... JSON.parse(null) returns null, and typeof null === "object",
                // so we must check for that, too. Thankfully, null is falsey, so this suffices:
                if (o && typeof o === "object") {
                    return o;
                }
            } catch (e) {
                return fallback;
            }

            return fallback;
        }

        function parseTwinData(twinData) {
            if ((typeof twinData?.ColumnJSON === 'string' || twinData?.ColumnJSON instanceof String) && twinData?.ColumnJSON !== "[object Object]") {
                twinData.ColumnJSON = JSON.parse(twinData.ColumnJSON)
            }
            if ((typeof twinData?.ReadingsJSON === 'string' || twinData?.ReadingsJSON instanceof String) && twinData?.ReadingsJSON !== "[object Object]") {
                twinData.ReadingsJSON = JSON.parse(twinData.ReadingsJSON)
            }
            if ((typeof twinData?.ProcessJSON === 'string' || twinData?.ProcessJSON instanceof String) && twinData?.ProcessJSON !== "[object Object]") {
                twinData.ProcessJSON = JSON.parse(twinData.ProcessJSON)
            }
            if ((typeof twinData?.Source === 'string' || twinData?.Source instanceof String) && twinData?.Source !== "[object Object]") {
                twinData.Source = JSON.parse(twinData.Source)
            }
            return twinData
        }

        return {

            areEqual: areEqual,
            isValid: isValid,
            flattenObject: flattenObject,
            clone: clone,
            isObject: isObject,
            freez: freez,
            getAllObjectPaths: getAllObjectPaths,
            noArrayClone: noArrayClone,
            tryParse: tryParse,
            parseTwinData: parseTwinData
        }

    }()
    /**
     *    This set of functions allows to work with tree
     *    @type {{recursiveSearch: (function(*, {key: string}, *): [number,number]|[number]|*), getTreeFromFlatData: ((function({flatData: !Object[], getKey?: !Function, getParentKey?: !Function, rootKey?: (string|number)}): Object[])|*)}}
     */
    const tree = function () {

        /**
         * Generate a tree structure from flat data.
         * @link https://github.com/frontend-collective/react-sortable-tree/blob/master/src/utils/tree-data-utils.js
         * @param {!Object[]} flatData
         * @param {!function=} getKey - Function to get the key from the nodeData
         * @param {!function=} getParentKey - Function to get the parent key from the nodeData
         * @param {string|number=} rootKey - The value returned by `getParentKey` that corresponds to the root node.
         *                                  For example, if your nodes have id 1-99, you might use rootKey = 0
         *
         * @return {Object[]} treeData - The flat data represented as a tree
         */
        function getTreeFromFlatData({
                                         flatData,
                                         getKey = node => node.id,
                                         getParentKey = node => node.parentId,
                                         rootKey = '0',
                                     }) {
            if (!flatData) {
                return [];
            }

            const childrenToParents = {};
            flatData.forEach(child => {
                const parentKey = getParentKey(child);

                if (parentKey in childrenToParents) {
                    childrenToParents[parentKey].push(child);
                } else {
                    childrenToParents[parentKey] = [child];
                }
            });

            if (!(rootKey in childrenToParents)) {
                return [];
            }

            const trav = parent => {
                const parentKey = getKey(parent);
                if (parentKey in childrenToParents) {
                    return {
                        ...parent,
                        children: childrenToParents[parentKey].map(child => trav(child)),
                    };
                }

                return {...parent};
            };

            return childrenToParents[rootKey].map(child => trav(child));
        }

        /**
         * This function searches in a tree for a specific value
         *
         * @param  treeObject - The tree
         * @param {{key: string}} searchFor - The key and value to search for
         * @param childKeyName - The name of the children in the tree
         * @return {number[]}
         */
        function recursiveSearch(treeObject, searchFor, childKeyName) {


            function recursivePart(treeObject, searchForKey, searchForValue = "", childKeyName) {
                let index = 0;

                for (const key in treeObject) {
                    const value = treeObject[key];

                    if (Array.isArray(value)) {

                        const array = value;
                        for (let i = 0; i < array.length; i++) {

                            if (objectPath.get(array, i + "." + searchForKey) === searchForValue.toString()) {
                                return [index, i];
                            }
                        }
                    } else if (typeof value === "object") {
                        if ((objectPath.get(value, searchForKey) ?? "").toString() === searchForValue.toString()) {
                            return [index];
                        }
                        const result = recursivePart(value[childKeyName], searchForKey, searchForValue, childKeyName);
                        if (Array.isArray(result)) {
                            return [index].concat(result);
                        }
                    }

                    index++;
                }
            }

            let searchForKey = Object.keys(searchFor)[0]
            let searchForValue = Object.values(searchFor)[0]

            return recursivePart(treeObject, searchForKey, searchForValue, childKeyName)


        }


        function getAllObjectPaths(object, currentPath = '', childKeyName = "childs") {
            let returnValue = []
            for (const key in object) {
                let currentTempPath = currentPath + key
                const value = object[key];
                returnValue.push(currentTempPath)
                if (Array.isArray(value)) {

                } else if (typeof object === "object") {
                    returnValue = [...getAllObjectPaths(value[childKeyName], currentTempPath + '.' + childKeyName + '.', childKeyName), ...returnValue];
                }
            }
            return returnValue
        }

        function getFlatData(treeObject, childKeyName = "childs") {
            let allObjectPaths = getAllObjectPaths(treeObject, "", childKeyName)
            let output = allObjectPaths.map(path => objectPaths.get(treeObject, path))
            return output

        }


        function getAllTwinIdsFromEntities(entitys) {
            // get the ids
            let twinIdsInTable = entitys.map(entity => {
                // if the child is of type tableDetails go throw its tableDetails_line
                if (entity.type === "tableDetails") {
                    // tableDetails_line
                    return entity.childs[0].childs.map(entity => entity.twinId).filter(value => value !== undefined)
                }
                return entity.twinId
            }).reduce((accumulator, currentValue) => {
                if (Array.isArray(currentValue)) {
                    return [...accumulator, ...currentValue]
                } else {
                    return [...accumulator, currentValue]
                }
            }, [])

            // remove undefined value
            twinIdsInTable = twinIdsInTable.filter(value => value !== undefined)

            // remove duplicates
            twinIdsInTable = Util.arrays.removeDuplicates(twinIdsInTable)

            return twinIdsInTable
        }

        // return the functions
        return {

            getTreeFromFlatData: getTreeFromFlatData,
            recursiveSearch: recursiveSearch,
            getAllObjectPaths: getAllObjectPaths,
            getFlatData: getFlatData,
            getAllTwinIdsFromEntities: getAllTwinIdsFromEntities
        }

    }()
    /**
     *    This set of functions allows to work with tree
     *    @type {{debounce: debounce, throttle: throttle}}
     */
    const performance = function () {


        /**
         * Call the function only after the delay of the last element has elapsed.
         * @param func
         * @param delay {number} time in ms
         * @returns {(function(): void)|*}
         */
        function debounce(func, delay) {
            let inDebounce
            return function () {
                if (inDebounce) clearTimeout(inDebounce)
                inDebounce = setTimeout(() => {
                    func.apply(this, arguments)
                }, delay)
            }
        }


        /**
         * Limiting the call of a function to an interval of x ms
         * @param func
         * @param delay {number} time in ms
         * @returns {(function(): void)|*}
         */
        function throttle(func, delay) {
            let inThrottle = false;
            return function () {
                if (!inThrottle) {
                    func.apply(this, arguments);
                    inThrottle = true;
                    setTimeout(() => (inThrottle = false), delay);
                }
            };
        }


        // return the functions
        return {

            debounce: debounce, throttle: throttle
        }

    }()
    /**
     *    This set of functions allows to work with DOM nodes
     *    @param {Node} domNode
     *
     */
    const domNode = function (domNode) {

        /**
         * This function gives back the node and its childes as a JSON
         */
        function getAsJSON() {

            const Element = e => ({
                toJSON: () => ({
                    tagName:
                    e.tagName,
                    textContent:
                    e.textContent,
                    attributes:
                        Array.from(e.attributes, ({
                                                      name,
                                                      value
                                                  }) => ({[name]: value})).reduce(function (result, current) {
                            return Object.assign(result, current);
                        }, {}),
                    children:
                        Array.from(e.children, Element)
                })
            })

            return JSON.parse(JSON.stringify(Element(domNode), null, '  '))
        }


        function attention(className, style, duration = 200) {

            if (domNode !== null) {

                if (!domNode.hasAttention) {
                    domNode.hasAttention = true

                    let originalStyle = JSON.parse(JSON.stringify(domNode.style))
                    let originalClassName = JSON.parse(JSON.stringify(domNode.className))
                    domNode.style = {...domNode.style, ...style}
                    domNode.className = domNode.className + " " + className
                    setTimeout(() => {
                        domNode.style = originalStyle
                        domNode.className = originalClassName
                        domNode.hasAttention = false

                    }, duration)


                }

            }
        }


        function focusInput(value, startValue) {
            if (domNode !== undefined && domNode !== null) {
                if (value === startValue && startValue !== undefined) {
                    domNode.focus();
                    domNode.select();
                } else {
                    domNode.focus();
                    domNode.setSelectionRange(-1, -1);
                }

            }
        }


        // return the functions
        return {
            getAsJSON: getAsJSON,
            attention: attention,
            focusInput: focusInput
        }

    }

    /**
     *    This set of functions allows to work with colors
     *    @type {{rgbaToHex: (function(*): string), getTextColorForBackground: (function(string): string), applyColorOpacity: (function(*, *): string)}}
     */
    const color = function () {

        /**
         * This function returns the color of the text
         * @param {string} hexColor - The hex value of the background color
         * @link https://gomakethings.com/dynamically-changing-the-text-color-based-on-background-color-contrast-with-vanilla-js/
         * @return { 'black' | 'white'}
         */
        function getTextColorForBackground(hexColor = "#ffff") {

            // If a leading # is provided, remove it
            if (hexColor.slice(0, 1) === '#') {
                hexColor = hexColor.slice(1);
            }

            // If a three-character hexcode, make six-character
            if (hexColor.length === 3) {
                hexColor = hexColor.split('').map(function (hex) {
                    return hex + hex;
                }).join('');
            }

            // Convert to RGB value
            let r = parseInt(hexColor.substr(0, 2), 16);
            let g = parseInt(hexColor.substr(2, 2), 16);
            let b = parseInt(hexColor.substr(4, 2), 16);

            // Get YIQ ratio
            let yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
            // Check contrast
            return (yiq >= 140) ? 'rgb(68 64 60)' : 'white'; // back = stone 700
        }

        /**
         * This function return returns a new color based on the input and the target opacity
         * @param hexCode
         * @param opacity
         * @return {string}
         */
        function applyColorOpacity(hexCode = "#ffff", opacity = 1) {
            let hex = hexCode.replace('#', '');

            if (hex.length === 3) {
                hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
            }

            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);

            return `rgba(${r},${g},${b},${opacity / 100})`;
        }

        /**
         * This functions converts rgba colors to hex
         * @param rgba
         * @return {string|string}
         */
        function rgbaToHex(rgba) {
            let sep = rgba.indexOf(",") > -1 ? "," : " ";
            rgba = rgba.substr(5).split(")")[0].split(sep);

            // Strip the slash if using space-separated syntax
            if (rgba.indexOf("/") > -1)
                rgba.splice(3, 1);

            for (let R in rgba) {
                let r = rgba[R];
                if (r.indexOf("%") > -1) {
                    let p = r.substr(0, r.length - 1) / 100;

                    if (R < 3) {
                        rgba[R] = Math.round(p * 255);
                    } else {
                        rgba[R] = p;
                    }
                }
            }
            let r = (+rgba[0]).toString(16),
                g = (+rgba[1]).toString(16),
                b = (+rgba[2]).toString(16),
                a = Math.round(+rgba[3] * 255).toString(16);

            if (r.length === 1)
                r = "0" + r;
            if (g.length === 1)
                g = "0" + g;
            if (b.length === 1)
                b = "0" + b;
            if (a.length === 1)
                a = "0" + a;

            return "#" + r + g + b + a;
        }

        function randomColor() {
            return "#" + Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0");
        }


        function hexToRgb(hex = "") {
            // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
            let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
            hex = hex.replace(shorthandRegex, function (m, r, g, b) {
                return r + r + g + g + b + b;
            });

            let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            } : {
                r: 0,
                g: 0,
                b: 0
            };
        }

        function colorNameToHex(colorName) {
            // see https://www.sttmedia.com/colornames
            let colorMap = {
                // black
                "black": "#000000", // English
                "schwarz": "#000000", // German
                "negro": "#000000", // Spanish
                "noir": "#000000", // French
                // Gray
                "gray": "#808080", // English
                "grau": "#808080", // German
                "gris": "#808080", // Spanish & French
                // Silver
                "silver": "#C0C0C0", // English
                "silber": "#C0C0C0", // German
                "plata": "#C0C0C0", // Spanish
                "argent": "#C0C0C0", // French
                // White
                "white": "#FFFFFF", // English
                "weiß": "#FFFFFF", // German
                "blanco": "#FFFFFF", // Spanish
                "blanc": "#FFFFFF", // French
                // Navy
                "navy": "#000080", // English
                "marinenblau": "#000080", // German
                // Blue
                "blue": "#0000FF", // English
                "blau": "#0000FF", // German
                "azul": "#0000FF", // Spanish
                "bleu": "#0000FF", // French
                // Green
                "green": "#008000", // English
                "grün": "#008000", // German
                "verde": "#008000", // Spanish
                "vert": "#008000", // French
                // Red
                "red": "#FF0000", // English
                "rot": "#FF0000", // German
                "rojo": "#FF0000", // Spanish
                "rouge": "#FF0000", // French
                // Purple
                "purple": "#FF00FF", // English
                "purpur": "#FF00FF", // German
                "púrpura": "#FF00FF", // Spanish
                "pourpre": "#FF00FF", // French
                // Yellow
                "yellow": "#FFFF00", // English
                "gelb": "#FFFF00", // German
                "amarillo": "#FFFF00", // Spanish
                "jaune": "#FFFF00", // French

            }
            return list.matchList(colorName, colorMap)
        }

        // return the functions
        return {
            colorNameToHex: colorNameToHex,
            randomColor: randomColor,
            hexToRgb: hexToRgb,
            getTextColorForBackground: getTextColorForBackground,
            applyColorOpacity: applyColorOpacity,
            rgbaToHex: rgbaToHex
        }

    }()
    /**
     *    This set of functions allows to work with colors
     *    @type {{levenshteinDistance: (function(string=, string=): *)}}
     */
    const algo = function () {

        /**
         * This function uses the Levenshtein distance algorithm to get a score for the similarity of two stings source  https://www.tutorialspoint.com/levenshtein-distance-in-javascript
         * @return A integer 0 is an exact match than the numbers go up
         * @link https://en.wikipedia.org/wiki/Levenshtein_distance
         * @param strOne {string}
         * @param strTwo {string}
         * @returns number
         */
        function levenshteinDistance(strOne = '', strTwo = '') {

            strOne = strOne.toString() || ""
            strTwo = strTwo.toString() || ""

            return (100 - quickScore(strOne, strTwo) * 100)
            const track = Array((strTwo.length + 1)).fill(null).map(() =>
                Array((strOne.length + 1)).fill(null));
            for (let i = 0; i <= strOne.length; i += 1) {
                track[0][i] = i;
            }
            for (let j = 0; j <= strTwo.length; j += 1) {
                track[j][0] = j;
            }
            for (let j = 1; j <= strTwo.length; j += 1) {
                for (let i = 1; i <= strOne.length; i += 1) {
                    const indicator = strOne[i - 1] === strTwo[j - 1] ? 0 : 1;
                    track[j][i] = Math.min(
                        track[j][i - 1] + 1, // deletion
                        track[j - 1][i] + 1, // insertion
                        track[j - 1][i - 1] + indicator, // substitution
                    );
                }
            }
            return track[strTwo.length][strOne.length];
        }


        /**
         *
         *
         * SOURCE https://github.com/fwextensions/quick-score
         *
         * @param string
         * @param query
         * @returns {number|number}
         */
        function quickScore(string = "", query = "") {

            /**
             * A class representing a half-open interval of characters.  A range's `location`
             * property and `max()` value can be used as arguments for the `substring()`
             * method to extract a range of characters.
             */
            class Range {
                /**
                 * @memberOf Range.prototype
                 * @member {number} location  Starting index of the range.
                 */

                /**
                 * @memberOf Range.prototype
                 * @member {number} length  Number of characters in the range.
                 */

                /**
                 * @param {number} [location=-1]  Starting index of the range.
                 * @param {number} [length=0]  Number of characters in the range.
                 */
                constructor(
                    location = -1,
                    length = 0) {
                    this.location = location;
                    this.length = length;
                }


                /* eslint no-inline-comments: 0 */

                /**
                 * Gets the end index of the range, which indicates the character
                 * immediately after the last one in the range.
                 *
                 * @returns {number}
                 */
                /**
                 * Sets the end index of the range, which indicates the character
                 * immediately after the last one in the range.
                 *
                 * @param {number} [value]  End of the range.
                 *
                 * @returns {number}
                 */
                max(
                    value) {
                    if (typeof value == "number") {
                        this.length = value - this.location;
                    }

                    // the NSMaxRange() function in Objective-C returns this value
                    return this.location + this.length;
                }


                /**
                 * Returns whether the range contains a location >= 0.
                 *
                 * @returns {boolean}
                 */
                isValid() {
                    return (this.location > -1);
                }


                /**
                 * Returns an array of the range's start and end indexes.
                 *
                 *
                 */
                toArray() {
                    return [this.location, this.max()];
                }


                /**
                 * Returns a string representation of the range's open interval.
                 *
                 * @returns {string}
                 */
                toString() {
                    if (this.location === -1) {
                        return "invalid range";
                    } else {
                        return "[" + this.location + "," + this.max() + ")";
                    }
                }
            }

            // setup
            let stringRange = new Range(0, string.length)
            let transformedString = string.toLocaleLowerCase()
            let transformedQuery = query.toLocaleLowerCase()
            const ignoredScore = 0.9
            const skippedScore = 0.15

            const maxIterations = Math.pow(2, 16)
            const wordSeparators = "-/\\:()<>%._=&[]+ \t\n\r"
            const uppercaseLetters = (() => {
                const charCodeA = "A".charCodeAt(0);
                const uppercase = [];

                for (let i = 0; i < 26; i++) {
                    uppercase.push(String.fromCharCode(charCodeA + i));
                }

                return uppercase.join("");
            })()

            const QSConfigDefaults = {
                longStringLength: 150,
                maxMatchStartPct: 0.15,
                minMatchDensityPct: 0.75,
                maxMatchDensityPct: 0.95,
                beginningOfStringPct: 0.1
            };
            const skipReduction = function (
                string,
                fullMatchedRange) {
                const len = string.length;
                const isShortString = len <= QSConfigDefaults.longStringLength;
                const matchStartPercentage = fullMatchedRange.location / len;

                return isShortString || matchStartPercentage < QSConfigDefaults.maxMatchStartPct;
            }

            const adjustRemainingScore = function (
                string,
                query,
                remainingScore,
                skippedSpecialChar,
                searchRange,
                remainingSearchRange,
                matchedRange,
                fullMatchedRange) {
                const isShortString = string.length <= QSConfigDefaults.longStringLength;
                const matchStartPercentage = fullMatchedRange.location / string.length;
                let matchRangeDiscount = 1;
                let matchStartDiscount = (1 - matchStartPercentage);

                // discount the remainingScore based on how much larger the match is
                // than the query, unless the match is in the first 10% of the
                // string, the match range isn't too sparse and the whole string is
                // not too long.  also only discount if we didn't skip any whitespace
                // or capitals.
                if (!skippedSpecialChar) {
                    matchRangeDiscount = query.length / fullMatchedRange.length;
                    matchRangeDiscount = (isShortString &&
                        matchStartPercentage <= QSConfigDefaults.beginningOfStringPct &&
                        matchRangeDiscount >= QSConfigDefaults.minMatchDensityPct) ?
                        1 : matchRangeDiscount;
                    matchStartDiscount = matchRangeDiscount >= QSConfigDefaults.maxMatchDensityPct ?
                        1 : matchStartDiscount;
                }

                // discount the scores of very long strings
                return remainingScore *
                    Math.min(remainingSearchRange.length, QSConfigDefaults.longStringLength) *
                    matchRangeDiscount * matchStartDiscount;
            }

            const getRangeOfSubstring = function (
                string,
                query,
                searchRange) {
                const index = string.indexOf(query, searchRange.location);
                const result = new Range();

                if (index > -1 && index < searchRange.max()) {
                    result.location = index;
                    result.length = query.length;
                }

                return result;
            }

            let iterations = 0;

            if (query) {
                return calcScore(stringRange, new Range(0, query.length), new Range());
            } else {
                // empty score
                return 0.9;
            }


            function calcScore(searchRange, queryRange, fullMatchedRange) {

                if (!queryRange.length) {
                    // deduct some points for all remaining characters
                    return ignoredScore;
                } else if (queryRange.length > searchRange.length) {
                    return 0;
                }

                for (let i = queryRange.length; i > 0; i--) {
                    if (iterations > maxIterations) {
                        // a long query that matches the string except for the last
                        // character can generate 2^queryLength iterations of this
                        // loop before returning 0, so short-circuit that when we've
                        // seen too many iterations (bit of an ugly kludge, but it
                        // avoids locking up the UI if the user somehow types an
                        // edge-case query)
                        return 0;
                    }

                    iterations++;

                    const querySubstring = transformedQuery.substring(queryRange.location, queryRange.location + i);
                    // reduce the length of the search range by the number of chars
                    // we're skipping in the query, to make sure there's enough string
                    // left to possibly contain the skipped chars
                    const matchedRange = getRangeOfSubstring(transformedString, querySubstring,
                        new Range(searchRange.location, searchRange.length - queryRange.length + i));

                    if (!matchedRange.isValid()) {
                        // we didn't find the query substring, so try again with a
                        // shorter substring
                        continue;
                    }

                    if (!fullMatchedRange.isValid()) {
                        fullMatchedRange.location = matchedRange.location;
                    } else {
                        fullMatchedRange.location = Math.min(fullMatchedRange.location, matchedRange.location);
                    }

                    fullMatchedRange.max(matchedRange.max());


                    const remainingSearchRange = new Range(matchedRange.max(), searchRange.max() - matchedRange.max());
                    const remainingQueryRange = new Range(queryRange.location + i, queryRange.length - i);
                    const remainingScore = calcScore(remainingSearchRange, remainingQueryRange, fullMatchedRange);

                    if (remainingScore) {
                        let score = remainingSearchRange.location - searchRange.location;
                        // default to true since we only want to apply a discount if
                        // we hit the final else clause below, and we won't get to
                        // any of them if the match is right at the start of the
                        // searchRange
                        let skippedSpecialChar = true;
                        const useSkipReduction = skipReduction(string, fullMatchedRange);

                        if (matchedRange.location > searchRange.location) {
                            // some letters were skipped when finding this match, so
                            // adjust the score based on whether spaces or capital
                            // letters were skipped
                            if (useSkipReduction &&
                                wordSeparators.indexOf(string[matchedRange.location - 1]) > -1) {
                                for (let j = matchedRange.location - 2; j >= searchRange.location; j--) {
                                    if (wordSeparators.indexOf(string[j]) > -1) {
                                        score--;
                                    } else {
                                        score -= skippedScore;
                                    }
                                }
                            } else if (useSkipReduction &&
                                uppercaseLetters.indexOf(string[matchedRange.location]) > -1) {
                                for (let j = matchedRange.location - 1; j >= searchRange.location; j--) {
                                    if (uppercaseLetters.indexOf(string[j]) > -1) {
                                        score--;
                                    } else {
                                        score -= skippedScore;
                                    }
                                }
                            } else {
                                // reduce the score by the number of chars we've
                                // skipped since the beginning of the search range
                                score -= matchedRange.location - searchRange.location;
                                skippedSpecialChar = false;
                            }
                        }

                        score += adjustRemainingScore(string,
                            query, remainingScore, skippedSpecialChar, searchRange,
                            remainingSearchRange, matchedRange, fullMatchedRange);
                        score /= searchRange.length;

                        return score;
                    }
                }

                return 0;
            }
        }

        // return the functions
        return {
            levenshteinDistance: levenshteinDistance,
        }

    }()

    /**
     *    This set of functions allows to work with colors
     *    @type {{get: (function(object= test, string=): *),coalesce: (function(object= test, string=): *),empty: (function(object= test, string=): *),set: (function(object= test, string=): *),insert: (function(object= test, string=): *),push: (function(object= test, string=): *),ensureExists: (function(object= test, string=): *),del: (function(object= test, string=): *)}}
     */
    const objectPaths = function () {
        return objectPath


    }()
    const path = function () {
        function join(...input) {
            return input.join('/').replace(/\/{2,}/, '/')
        }

        function basename(filePath = "") {
            return filePath.replace(/^.*[\\\/]/, '')
        }

        // return the functions
        return {
            join: join,
            basename: basename
        }

    }()

    function log(variable) {
        new Error("Not correctly  implemented")

        console.log(Object.keys({variable})[0] + ":" + variable)
    }

    const time = function () {
        function sleep(ms) {
            return new Promise((resolve) => {
                setTimeout(resolve, ms);
            });
        }

        /**
         * This returns as the current unix timestamp in seconds
         */
        function now() {
            return (new Date()).getTime() / 1000
        }

        return {sleep: sleep, now: now}
    }()


    const files = function () {
        /**
         *
         * @param fileName
         * @param fileData
         */
        function fileDownloadInBrowser(fileName = "FileWithNoName.empty", fileData) {
            // // download the files
            let url = "data:application/pdf;base64," + fileData;
            let a = document.createElement('a');
            a.href = url;
            // remove file path form name
            a.download = fileName.replace(/^.*[\\\/]/, '');
            document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
            a.click();
            a.remove();  //afterwards we remove the element again
        }


        function openFileDialog(accept = "*", multiple = false, allowDirectory = false) {
            return new Promise((resolve) => {

                let input = document.createElement('input');
                input.type = 'file';
                input.multiple = multiple;
                input.webkitdirectory = allowDirectory;
                input.accept = accept;

                input.onchange = (e) => {

                    // getting a hold of the file reference
                    let files = Array.from(e.target.files);

                    resolve(files)
                    // // setting up the reader
                    // let reader = new FileReader();
                    // reader.readAsText(file, 'UTF-8');
                    //
                    // // here we tell the reader what to do when it's done reading...
                    // reader.onload = readerEvent => {
                    //     let content = readerEvent.target.result; // this is the content!
                    //     console.log(content);
                    //     input.remove();
                    //
                    // }

                }

                input.oncancel = (e) => {
                    input.remove();
                }

                input.click();


            })
        }

        return {fileDownloadInBrowser: fileDownloadInBrowser, openFileDialog: openFileDialog}
    }()

    const bytes = function () {
        /**
         *
         * @param fileName
         * @param fileData
         */
        function formatBytes(bytes, decimals = 2) {
            if (bytes === 0) return '0 Bytes';

            const k = 1024;
            const dm = decimals < 0 ? 0 : decimals;
            const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

            const i = Math.floor(Math.log(bytes) / Math.log(k));

            return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
        }

        return {formatBytes: formatBytes}
    }()

    const clipboard = function () {

        function set(text) {
            navigator.clipboard.writeText(text).then(
                () => {
                    /* clipboard successfully set */
                },
                (error) => {
                    console.error(error)
                    Notification.createError("The Clipboard could not be written to")
                    /* clipboard write failed */
                }
            );
        }

        async function getJSON() {
            return new Promise(async (resolve) => {
                try {
                    const permission = await navigator.permissions.query({name: 'clipboard-read'});
                    if (permission.state !== 'denied') {

                        const clipboardContents = await navigator.clipboard.readText();

                        try {
                            let json = JSON.parse(clipboardContents)
                            resolve(json)
                        } catch (error) {
                            console.error(error.message);
                            resolve(null);

                        }

                    } else {
                        Notification.createWarning("The Clipboard access was denied")
                        resolve(null);
                    }
                } catch (error) {
                    console.error(error.message);
                    resolve(null);
                }
            })
        }

        return {set: set, getJSON: getJSON}
    }()

    const location = function () {

        function getFromGPS() {
            return new Promise((resolve) => {


                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition((data) => {
                        resolve(data)
                    });
                } else {
                    Notification.createError("Geolocation is not supported by this browser.")
                    resolve("Geolocation is not supported by this browser.")
                }
            })
        }

        function getFromIP(text) {

        }


        return {getFromGPS: getFromGPS}
    }()

    const printer = function () {

        function printFile(data) {
            return new Promise((resolve) => {
                let pdfUrl = URL.createObjectURL(data);
                let iframe = document.createElement('iframe');
                document.body.appendChild(iframe);

                iframe.style.display = 'none';
                iframe.onload = () => {

                    setTimeout(async () => {
                        iframe.focus();
                        iframe.contentWindow.print();

                        // this timeout is needed to prevent the iframe form being unmounted before the dialog is opened
                        setTimeout(() => {
                            // this mouseover event does firer after the dialog is opened
                            // this is not god but at the time the only thing that is reliably working
                            // DO NOT try to use onafterprint it will not work
                            let event = 'mouseover'
                            // this is 🤬 haki
                            const handler = () => {
                                // Make sure the event only happens once.
                                window.removeEventListener(event, handler)
                                iframe.remove()
                            }

                            window.addEventListener(event, handler)

                        }, 500)


                        resolve()
                    }, 1);


                };

                iframe.src = pdfUrl;
            })
        }


        return {printFile: printFile}
    }()


    const speech = function () {

        function textToSpeech(text, language = 'en') {
            const synth = window?.speechSynthesis;

            // check if the speechSynthesis feature is  supported
            if (synth !== undefined) {

                let msg = new SpeechSynthesisUtterance(text);
                // let voices = window.speechSynthesis.getVoices();
                msg.lang = language;
                //msg.text = text;
                synth.speak(msg);
            } else {
                Notification.createInfo("We are sorry, but your browser does not support text to speech!", "Try using Chrome and check the text-to-speech settings in our operating system.");
            }

        }

        return {textToSpeech: textToSpeech}
    }()

    const concurrency = function () {

        /**
         *
         * @returns {{release: release, acquire: (function(string=): Promise<unknown>)}}
         */
        const lock = () => {
            let locked = {};
            const ee = new EventEmitter();
            ee.setMaxListeners(0);

            return {
                /**
                 *
                 * @param key
                 * @returns {Promise<*>}
                 */
                acquire: (key = "main") =>
                    new Promise(resolve => {
                        if (!locked[key]) {
                            locked[key] = true;
                            return resolve();
                        }

                        const tryAcquire = () => {
                            if (!locked[key]) {
                                locked[key] = true;
                                ee.removeListener(key, tryAcquire);
                                return resolve();
                            }
                        };

                        ee.on(key, tryAcquire);
                    }),

                /**
                 *
                 * @param key
                 */
                release: (key = "main") => {
                    Reflect.deleteProperty(locked, key);
                    setTimeout(() => ee.emit(key), 2);
                },

                isLocked: (key = "main") => {
                    return !locked[key]
                }
            };
        }
        return {lock: lock}

    }()
    /**
     *
     * @type {{newId: (function(*): string)}}
     */
    const unique = function () {
        // we have the use this os it is compatible with all the apis
        const alphabet = '0123456789abcdefghijklmnopqrstuvwxyz';
        const nanoid = customAlphabet(alphabet, 21);

        /**
         *
         * @param {number} [size=12]
         * @returns {string}
         */
        function newId(size) {
            //  size: 12 ~1 a thousand years of work are needed in order to have a 1% probability of at least one collision.
            //  size: 9 ~2 years of work are needed in order to have a 1% probability of at least one collision.
            return nanoid(size ?? 12)

        }


        /**
         *
         * @returns {string}
         */
        function newInternalId() {
            //  size: 9 ~2 years of work are needed in order to have a 1% probability of at least one collision.
            return nanoid(9)

        }


        /**
         *
         * @returns {string}
         */
        function newGlobalId() {
            //   size: 12 ~1 a thousand years of work are needed in order to have a 1% probability of at least one collision.
            return nanoid(12)

        }


        // return the functions
        return {
            newId: newId,
            newInternalId: newInternalId,
            newGlobalId: newGlobalId,

        }

    }()


    const sessionStore = function (key) {


        function set(object) {
            localStorage.setItem(key, JSON.stringify(object));

        }

        function get(callback) {
            let storageItem = localStorage.getItem(key)
            if (storageItem !== undefined && storageItem !== null) {
                let storage = Util.objects.tryParse(storageItem, undefined)
                if (callback !== undefined && storage !== undefined) {
                    // This try catch is required to allow the application to recover from a corrupted browser cache.
                    try {
                        callback(storage)
                    } catch (e) {
                        ErrorHandler.recordTrace(e)
                    }
                }
                return storage
            }
            return undefined
        }


        // return the functions
        return {
            set: set,
            get: get

        }

    }


    const device = function (key) {


        function getHardwareAcceleration() {
            let canvas = document.createElement('canvas');
            let gl;
            let debugInfo;
            let vendor;
            let renderer;

            try {
                gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            } catch (e) {
                return "Error: " + e.message;
            }

            if (gl) {
                debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
                vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
                renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
            }

            return renderer
        }


        // return the functions
        return {
            getHardwareAcceleration: getHardwareAcceleration,

        }

    }()

    const react = function (key) {


        // a function to retry loading a chunk to avoid chunk load error for out of date code
        function lazyRetry(componentImport) {
            return new Promise((resolve, reject) => {
                // check if the window has already been refreshed
                const hasRefreshed = JSON.parse(
                    window.sessionStorage.getItem('retry-refreshed') || 'false'
                );
                // try to import the component
                componentImport().then((component) => {
                    window.sessionStorage.setItem('retry-refreshed', 'false'); // success so reset the refresh
                    resolve(component);
                }).catch((error) => {
                    if (!hasRefreshed) { // not been refreshed yet
                        window.sessionStorage.setItem('retry-refreshed', 'true'); // we are now going to refresh
                        return window.location.reload(); // refresh the page
                    }
                    reject(error); // Default error behaviour as already tried refresh
                });
            });
        }


        // return the functions
        return {
            lazyRetry: lazyRetry,

        }

    }()

    const list = function () {


        function matchList(inputString, listMap) {

            inputString = inputString.toLowerCase()

            let outputIcon = null
            let words = inputString.split(" ")
            words.map(word => {
                if (listMap[word] !== undefined) {
                    outputIcon = listMap[word]
                }
            })
            return outputIcon
        }


        // return the functions
        return {
            matchList: matchList,

        }

    }()


    const process = function () {

        function processNameToIcon(processName) {
            let iconMap = {
                // create
                "new": "IoIosAdd", // English
                "create": "IoIosAdd", // English
                "neues": "IoIosAdd", // German
                "neuer": "IoIosAdd", // German
                "anlegen": "IoIosAdd", // German
                "erstellen": "IoIosAdd", // German
                "crear": "IoIosAdd", // Spanish
                "créer": "IoIosAdd", // French
                // delete
                "delete": "IoTrashOutline", // English
                "löschen": "IoTrashOutline", // German
                "borrar": "IoTrashOutline", // Spanish
                "supprimer": "IoTrashOutline", // French
                // file formats
                "excel": "SiMicrosoftexcel",
                "word": "SiMicrosoftword",
                "access": "SiMicrosoftaccess",
                // calculate
                "calculate": "IoCalculatorOutline", // English
                "berechnen": "IoCalculatorOutline", // German
                "ermitteln": "IoCalculatorOutline", // German
                "calcular": "IoCalculatorOutline", // Spanish
                "calculer": "IoCalculatorOutline", // French

            }


            return list.matchList(processName, iconMap)

        }

        return {processNameToIcon: processNameToIcon}
    }()

    return {
        arrays: arrays,
        string: string,
        printer: printer,
        tree: tree,
        location: location,
        performance: performance,
        clipboard: clipboard,
        log: log,
        domNode: domNode,
        path: path,
        color: color,
        algo: algo,
        objects: objects,
        objectPaths: objectPaths,
        time: time,
        files: files,
        list: list,
        process: process,
        number: number,
        bytes: bytes,
        concurrency: concurrency,
        unique: unique,
        speech: speech,
        sessionStore: sessionStore,
        device: device,
        react: react,
    }
}()


export {
    Util,
};